// src/pages/VendorDashboard.tsx
// Painel de Vendas para Vendedores - IDENTIDADE GRAIA

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
    BarChart3,
    Ticket,
    FileText,
    Wallet,
    Target,
    ArrowLeft
} from 'lucide-react';
import Toast from '../components/Toast';
import './VendorDashboard.css';

// Componentes das abas
import VendorOverview from '../components/vendor/VendorOverview';
import VendorCoupons from '../components/vendor/VendorCoupons';
import VendorSalesHistory from '../components/vendor/VendorSalesHistory';
import VendorPayments from '../components/vendor/VendorPayments';
import VendorGoals from '../components/vendor/VendorGoals';
import VendorWelcomeModal, { useVendorWelcome } from '../components/vendor/VendorWelcomeModal';


type VendorTab = 'overview' | 'coupons' | 'sales' | 'payments' | 'goals';

interface VendorStats {
    totalSales: number;
    totalCommission: number;
    availableBalance: number;
    activeCoupons: number;
    totalOrders: number;
}

interface Coupon {
    _id: string;
    code: string;
    discountPercent: number;
    timesUsed: number;
    active: boolean;
    isLink: boolean;
    productId?: {
        _id: string;
        title: string;
        type: string;
    };
    createdAt: string;
}

interface Sale {
    _id: string;
    productTitle: string;
    originalPriceCents: number;
    finalPriceCents: number;
    discountCents: number;
    vendorCommissionCents: number;
    couponCode?: string;
    paymentStatus: string;
    balanceStatus: 'pending' | 'available' | 'cancelled';
    daysSinceSale: number;
    createdAt: string;
}

const VendorDashboard: React.FC = () => {
    const navigate = useNavigate();
    const { isVendor } = useAuth();

    const [activeTab, setActiveTab] = useState<VendorTab>('overview');
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState<VendorStats | null>(null);
    const [coupons, setCoupons] = useState<Coupon[]>([]);
    const [sales, setSales] = useState<Sale[]>([]);
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
    const { showWelcome, dismissWelcome } = useVendorWelcome();

    const loadVendorData = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');

            // Carregar estatísticas
            const statsRes = await fetch('/api/vendor/stats', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (statsRes.ok) {
                const statsData = await statsRes.json();
                setStats(statsData);
            }

            // Carregar cupons
            const couponsRes = await fetch('/api/vendor/coupons', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (couponsRes.ok) {
                const couponsData = await couponsRes.json();
                setCoupons(couponsData.coupons || []);
            }

            // Carregar vendas
            const salesRes = await fetch('/api/vendor/sales', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (salesRes.ok) {
                const salesData = await salesRes.json();
                setSales(salesData.sales || []);
            }

        } catch (error) {
            console.error('Erro ao carregar dados:', error);
            showToast('Erro ao carregar dados do painel', 'error');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!isVendor) {
            navigate('/dashboard');
            return;
        }
        loadVendorData();
    }, [isVendor, navigate, loadVendorData]);

    const showToast = (message: string, type: 'success' | 'error' | 'info') => {
        setToast({ message, type });
    };

    const tabs = [
        { id: 'overview' as VendorTab, label: 'Visão Geral', icon: BarChart3 },
        { id: 'coupons' as VendorTab, label: 'Meus Cupons', icon: Ticket },
        { id: 'sales' as VendorTab, label: 'Histórico de Vendas', icon: FileText },
        { id: 'payments' as VendorTab, label: 'Pagamentos', icon: Wallet },
        { id: 'goals' as VendorTab, label: 'Metas', icon: Target },
    ];

    if (loading) {
        return (
            <div className="vndr-dashboard">
                <div className="vndr-loading">
                    <div className="vndr-spinner"></div>
                    <p>Carregando painel de vendas...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="vndr-dashboard">
            {/* Header */}
            <header className="vndr-header">
                <div className="vndr-header-container">
                    {/* Botão Voltar Direita */}
                    <div className="vndr-actions-section">
                        <button className="vndr-btn-back" onClick={() => navigate('/dashboard')} title="Voltar">
                            <ArrowLeft size={20} />
                        </button>
                    </div>

                    {/* Título Central */}
                    <div className="vndr-title-section">
                        <h1 className="vndr-main-title">Painel de Vendas</h1>
                        <p className="vndr-subtitle">GRAIA</p>
                    </div>

                    {/* Logo Esquerda */}
                    <div className="vndr-logo-box">
                        <img
                            src="/assets/graia-logo.png"
                            alt="GRAIA"
                            className="vndr-logo-img"
                        />
                    </div>
                </div>
            </header>

            {/* Tabs */}
            <div className="vndr-tabs-wrapper">
                <div className="vndr-tabs-container">
                    {tabs.map((tab) => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                className={`vndr-tab ${activeTab === tab.id ? 'vndr-tab-active' : ''}`}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                <Icon size={18} strokeWidth={1.5} />
                                <span>{tab.label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Content */}
            <div className="vndr-content">
                {activeTab === 'overview' && (
                    <VendorOverview
                        stats={stats}
                        sales={sales}
                        onShowToast={showToast}
                    />
                )}

                {activeTab === 'coupons' && (
                    <VendorCoupons
                        coupons={coupons}
                        onRefresh={loadVendorData}
                        onShowToast={showToast}
                    />
                )}

                {activeTab === 'sales' && (
                    <VendorSalesHistory
                        sales={sales}
                        onRefresh={loadVendorData}
                        onShowToast={showToast}
                    />
                )}

                {activeTab === 'payments' && (
                    <VendorPayments
                        stats={stats}
                        onShowToast={showToast}
                    />
                )}

                {activeTab === 'goals' && (
                    <VendorGoals
                        onShowToast={showToast}
                    />
                )}
            </div>

            {/* Toast */}
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}

            {showWelcome && (
                <VendorWelcomeModal
                    onClose={dismissWelcome}
                    onCreateCoupon={() => setActiveTab('coupons')}
                />
            )}
        </div>
    );
};

export default VendorDashboard;